import {action, computed, observable, makeObservable} from 'mobx'

export interface BulkEditPropertyProps {
    readonly name: string
    readonly groupName: string
    readonly isChanged: boolean
    readonly isVisible: boolean
    resetValue(): void
    applyValue(): void
    setVisibility(visibility: boolean): void
}

export abstract class BulkEditPropertyBase implements BulkEditPropertyProps {
    @observable protected _isChanged = false
    @computed get isChanged(): boolean {
        return this._isChanged
    }

    @observable protected _isVisible = true
    @computed get isVisible(): boolean {
        return this._isVisible
    }

    constructor(
        public readonly name: string,
        public readonly groupName: string,
        onValueChange?: (prop: BulkEditPropertyBase) => void,
        onApplyValue?: (prop: BulkEditPropertyBase) => void,
        isVisible = true
    ) {
        makeObservable(this)
        if (onValueChange !== undefined) {
            this.onValueChange = onValueChange
        }

        if (onApplyValue !== undefined) {
            this.onApplyValue = onApplyValue
        }

        this._isVisible = isVisible
    }

    protected onValueChange?: (prop: BulkEditPropertyBase) => void = undefined

    protected onApplyValue?: (prop: BulkEditPropertyBase) => void = undefined

    public setVisibility(visibility: boolean): void {
        this._isVisible = visibility
    }

    public applyValue(): void {
        if (this.onApplyValue !== undefined && this.isChanged) {
            this.onApplyValue(this)
        }
    }

    abstract resetValue(): void
}

export class NumberBulkEditProperty extends BulkEditPropertyBase {
    @observable public readonly defaultValue: number = 0

    @observable private _value?: number = undefined
    @computed get value(): number {
        if (this._value === undefined) {
            return this.defaultValue
        }

        return this._value
    }

    constructor(
        name: string,
        groupName: string,
        public readonly allowFloatingPoint = true,
        defaultValue: number,
        onValueChange?: (prop: BulkEditPropertyBase) => void,
        onApplyValue?: (prop: BulkEditPropertyBase) => void,
        isVisible = true
    ) {
        super(name, groupName, onValueChange, onApplyValue, isVisible)
        makeObservable(this)
        this.defaultValue = defaultValue
    }

    @action.bound
    public setValue(value: number): void {
        if (!this.allowFloatingPoint && value % 1 !== 0) {
            throw new Error('Value is not int!')
        }

        this._value = value
        this._isChanged = true

        if (this.onValueChange !== undefined) {
            this.onValueChange(this)
        }
    }

    @action.bound
    public resetValue(): void {
        this._value = this.defaultValue
        this._isChanged = false
    }
}

export interface EnumEntry {
    name: string
    value: string | number
}

export class EnumBulkEditProperty extends BulkEditPropertyBase {
    @observable private defaultKey = ''
    @observable private _selectedKey = ''

    @computed get selectedKey(): string {
        return this._selectedKey || this.defaultKey
    }

    @computed get selectedValue(): string | number {
        return this.enumMap.get(this.selectedKey)?.value || ''
    }

    private readonly enumMap: Map<string, EnumEntry>

    constructor(
        name: string,
        groupName: string,
        theEnum: {[name: string]: string | number},
        defaultValue?: string | number,
        onValueChange?: (prop: BulkEditPropertyBase) => void,
        onApplyValue?: (prop: BulkEditPropertyBase) => void,
        isVisible = true
    ) {
        super(name, groupName, onValueChange, onApplyValue, isVisible)

        makeObservable(this)

        this.enumMap = new Map<string, EnumEntry>(
            Object.keys(theEnum).map(key => [key, {name: key, value: theEnum[key]}])
        )

        if (defaultValue !== undefined) {
            const def = Array.from(this.enumMap.values()).find(v => v.value === defaultValue)
            if (def) {
                this.defaultKey = def.name
            }
        }
    }

    public [Symbol.iterator](): Iterator<EnumEntry> {
        return this.enumMap.values()
    }

    @action.bound
    public selectKey(key: string): void {
        this._selectedKey = key
        this._isChanged = true

        if (this.onValueChange !== undefined) {
            this.onValueChange(this)
        }
    }

    @action.bound
    public resetValue(): void {
        this._selectedKey = this.defaultKey
        this._isChanged = false
    }
}

export interface Option {
    label: string
    value: any
}

export class OptionBulkEditProperty extends BulkEditPropertyBase {
    @observable private _selectedKey?: string = undefined
    @computed get selectedKey(): string {
        return this._selectedKey || this.defaultOptionKey || ''
    }

    private readonly optionsMap: Map<string, Option>

    constructor(
        name: string,
        groupName: string,
        options: Map<string, any>,
        public readonly defaultOptionKey?: string,
        onValueChange?: (prop: BulkEditPropertyBase) => void,
        onApplyValue?: (prop: BulkEditPropertyBase) => void,
        isVisible = true
    ) {
        super(name, groupName, onValueChange, onApplyValue, isVisible)

        makeObservable(this)

        this.optionsMap = new Map<string, Option>(
            Array.from(options.entries()).map(([key, value]): [string, any] => [key, {label: key, value: value}])
        )
    }

    public [Symbol.iterator](): Iterator<Option> {
        return this.optionsMap.values()
    }

    public getValue(key: string): Option | undefined {
        return this.optionsMap.get(key)
    }

    @action.bound
    public selectKey(key: string): void {
        this._selectedKey = key
        this._isChanged = true

        if (this.onValueChange !== undefined) {
            this.onValueChange(this)
        }
    }

    @action.bound
    public resetValue(): void {
        this._selectedKey = this.defaultOptionKey
        this._isChanged = false
    }
}

export class MultiOptionBulkEditProperty extends BulkEditPropertyBase {
    @observable private _selectedKeys: Set<string> = new Set<string>()
    @computed get selectedKeys(): string[] {
        return Array.from(this._selectedKeys.keys())
    }

    @computed get selectedOptions(): Option[] {
        const opts = []
        for (const key of this._selectedKeys.keys()) {
            const option = this.getValue(key)
            if (option) {
                opts.push(option)
            }
        }

        return opts
    }

    private readonly optionsMap: Map<string, Option>

    constructor(
        name: string,
        groupName: string,
        options: Map<string, any>,
        private readonly defaultOptionKeys?: string[],
        onValueChange?: (prop: BulkEditPropertyBase) => void,
        onApplyValue?: (prop: BulkEditPropertyBase) => void,
        isVisible = true
    ) {
        super(name, groupName, onValueChange, onApplyValue, isVisible)

        makeObservable(this)

        this.optionsMap = new Map<string, Option>(
            Array.from(options.entries()).map(([key, value]): [string, any] => [key, {label: key, value: value}])
        )

        if (defaultOptionKeys !== undefined) {
            defaultOptionKeys.forEach(key => {
                this._selectedKeys.add(key)
            })
        }
    }

    public [Symbol.iterator](): Iterator<Option> {
        return this.optionsMap.values()
    }

    public getValue(key: string): Option | undefined {
        return this.optionsMap.get(key)
    }

    @action.bound
    public selectKeys(keys: string[]): void {
        this._selectedKeys.clear()

        keys.forEach(key => {
            if (!this._selectedKeys.has(key)) {
                this._selectedKeys.add(key)
            }
        })

        this._isChanged = true

        if (this.onValueChange !== undefined) {
            this.onValueChange(this)
        }
    }

    @action.bound
    public resetValue(): void {
        this.selectKeys(this.defaultOptionKeys || [])
        this._isChanged = false
    }
}
